import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Container, Card, Button, Alert, ListGroup, Spinner } from 'react-bootstrap';

function App() {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sheetNames, setSheetNames] = useState({});
  const [outputFilename, setOutputFilename] = useState('merged_excel');

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prevFiles => {
      const newFiles = [...prevFiles, ...acceptedFiles];
      // Initialize sheet names for new files
      const newSheetNames = { ...sheetNames };
      acceptedFiles.forEach(file => {
        if (!newSheetNames[file.name]) {
          newSheetNames[file.name] = '';
        }
      });
      setSheetNames(newSheetNames);
      return newFiles;
    });
  }, [sheetNames]);

  const handleRemoveFile = (index) => {
    const fileToRemove = files[index];
    setFiles(files.filter((_, i) => i !== index));
    setSheetNames(prevNames => {
      const newNames = { ...prevNames };
      delete newNames[fileToRemove.name];
      return newNames;
    });
  };

  const clearAllFiles = () => {
    setFiles([]);
    setSheetNames({});
  };

  const extractDateFromSheet = (buffer) => {
    // Use SheetJS to get correct dates
    const workbook = XLSX.read(buffer, { 
      type: 'array',
      cellDates: true,
      dateNF: 'm/d/yyyy'
    });
    
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const dateValues = {};

    // Collect all date values
    Object.keys(sheet).forEach(key => {
      if (key[0] !== '!') { // Skip metadata (keys starting with !)
        const cell = sheet[key];
        if (cell.v instanceof Date) {
          dateValues[key] = cell.v;
        }
      }
    });

    return dateValues;
  };

  const mergeExcelFiles = async () => {
    if (files.length === 0) return;
    if (!outputFilename.trim()) {
      alert('Please enter an output filename before merging');
      return;
    }

    setIsProcessing(true);
    try {
      // Create a new workbook with ExcelJS
      const mergedWorkbook = new ExcelJS.Workbook();
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const buffer = await file.arrayBuffer();
        
        // Get dates using SheetJS
        const dateValues = extractDateFromSheet(buffer);
        
        // Process with ExcelJS for styling
        const sourceWorkbook = new ExcelJS.Workbook();
        await sourceWorkbook.xlsx.load(buffer);
        
        // Get the first worksheet
        const sourceWorksheet = sourceWorkbook.worksheets[0];
        
        // Create a new worksheet for each file using custom name if provided
        const customName = sheetNames[file.name];
        const worksheetName = customName || `Sheet${i + 1}_${file.name.split('.')[0].substring(0, 20)}`;
        const targetWorksheet = mergedWorkbook.addWorksheet(worksheetName);
        
        // Copy column properties
        sourceWorksheet.columns.forEach((col, index) => {
          if (col) {
            targetWorksheet.getColumn(index + 1).width = col.width || 10;
          }
        });

        // Helper function to extract cell value and style
        function extractCellData(cell) {
          const data = {
            address: cell.address,
            style: { ...cell.style }
          };

          // Check if we have a date value for this cell from SheetJS
          const cellAddress = cell.address.replace(/\d+/, ''); // Get column letter
          const rowNum = cell.address.match(/\d+/)[0]; // Get row number
          const sheetJSAddress = cellAddress + rowNum;
          
          if (dateValues[sheetJSAddress]) {
            // Use the date from SheetJS
            data.value = dateValues[sheetJSAddress];
            if (!data.style.numFmt) {
              data.style.numFmt = 'm/d/yyyy';
            }
          } else {
            // Use ExcelJS value for non-dates
            data.value = cell.value;
          }

          return data;
        }

        // Collect row and cell data
        sourceWorksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          const rowData = {
            number: rowNumber,
            height: row.height,
            cells: []
          };

          // First pass: collect all cell data
          const cellsInRow = [];
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellData = extractCellData(cell);
            cellsInRow.push({
              colNumber,
              data: cellData,
              isMerged: cell.isMerged,
              masterAddress: cell.isMerged ? cell.master.address : null
            });
          });

          // Second pass: handle duplicate values from merged cells
          let lastValue = null;
          let lastColNumber = null;
          
          cellsInRow.forEach((cell) => {
            const currentValue = JSON.stringify(cell.data.value);
            
            if (cell.isMerged) {
              if (cell.masterAddress === cell.data.address) {
                lastValue = currentValue;
                lastColNumber = cell.colNumber;
                rowData.cells.push(cell.data);
              } else {
                const clearedData = { ...cell.data, value: null };
                rowData.cells.push(clearedData);
              }
            } else {
              if (currentValue === lastValue && cell.colNumber === lastColNumber + 1) {
                const clearedData = { ...cell.data, value: null };
                rowData.cells.push(clearedData);
              } else {
                lastValue = currentValue;
                lastColNumber = cell.colNumber;
                rowData.cells.push(cell.data);
              }
            }
          });

          // Apply row and cell data
          const targetRow = targetWorksheet.getRow(rowData.number);
          if (rowData.height) {
            targetRow.height = rowData.height;
          }

          rowData.cells.forEach(cellData => {
            const targetCell = targetWorksheet.getCell(cellData.address);
            targetCell.value = cellData.value;
            if (Object.keys(cellData.style).length > 0) {
              targetCell.style = cellData.style;
            }
          });

          targetRow.commit();
        });

        // Apply worksheet properties and views
        targetWorksheet.properties = sourceWorksheet.properties;
        targetWorksheet.views = sourceWorksheet.views;
      }

      // Generate the Excel file
      const buffer = await mergedWorkbook.xlsx.writeBuffer();
      
      // Create a Blob and trigger download
      const url = window.URL.createObjectURL(new Blob([buffer]));
      const link = document.createElement('a');
      link.href = url;
      // Use the outputFilename state and ensure it has .xlsx extension
      const finalFilename = outputFilename.endsWith('.xlsx') ? outputFilename : `${outputFilename}.xlsx`;
      link.setAttribute('download', finalFilename);
      link.click();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      alert(`Error merging files: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    }
  });

  return (
    <Container className="py-5">
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h2 className="mb-0">Excel File Merger</h2>
        </Card.Header>
        <Card.Body>
          <Alert variant="info" className="mb-4">
            <h5 className="alert-heading">Instructions:</h5>
            <ol className="mb-0">
              <li><strong>Enter Output Filename:</strong> Provide a name for your merged Excel file (required)</li>
              <li>Drag and drop Excel files here, or click to select files</li>
              <li>Optionally customize sheet names for each file</li>
              <li>Click 'Merge Files' when ready</li>
            </ol>
          </Alert>

          <Card className="mb-3">
            <Card.Body>
              <label htmlFor="outputFilename" className="form-label fw-bold">Output Filename <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control form-control-lg"
                id="outputFilename"
                value={outputFilename}
                onChange={(e) => setOutputFilename(e.target.value)}
                placeholder="Enter output filename (e.g., merged_excel)"
                required
              />
              <div className="form-text">The .xlsx extension will be added automatically if not included</div>
            </Card.Body>
          </Card>

          <div
            {...getRootProps()}
            className={`border rounded p-4 text-center mb-4 ${
              isDragActive ? 'bg-light border-primary' : ''
            }`}
            style={{ 
              borderStyle: 'dashed',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <input {...getInputProps()} />
            <div className="mb-3">
              <i className="bi bi-cloud-upload" style={{ fontSize: '2rem' }}></i>
            </div>
            {isDragActive ? (
              <p className="mb-0">Drop the Excel files here...</p>
            ) : (
              <p className="mb-0">Drag and drop Excel files here, or click to select files</p>
            )}
          </div>

          {files.length > 0 && (
            <>
              <h5 className="mb-3">Selected Files:</h5>
              <ListGroup className="mb-4">
                {files.map((file, index) => (
                  <ListGroup.Item key={file.name} className="d-flex justify-content-between align-items-center">
                    <div className="d-flex flex-column flex-grow-1 me-3">
                      <span>{file.name}</span>
                      <div className="mt-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Enter sheet name (optional)"
                          value={sheetNames[file.name] || ''}
                          onChange={(e) => setSheetNames(prev => ({
                            ...prev,
                            [file.name]: e.target.value
                          }))}
                        />
                      </div>
                    </div>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                    >
                      Remove
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}

          <div className="d-grid gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={mergeExcelFiles}
              disabled={files.length === 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Processing...
                </>
              ) : (
                'Merge Files'
              )}
            </Button>
            {files.length > 0 && (
              <Button
                variant="danger"
                size="lg"
                onClick={clearAllFiles}
                disabled={isProcessing}
              >
                Clear All Files
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default App;
